import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiBaseUrl } from '../firebase';
import { listMusicTracks } from '../utils/secureFunctions';
import { fetchMusicProxyBlobUrl } from '../utils/vpsApi';

export type MusicTrack = {
  id: string;
  title: string;
  artistName: string;
  artistId: string;
  audioURL: string;
  isPublic: boolean;
  allowReels: boolean;
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (track: MusicTrack) => void;
}

const MusicLibraryPicker: React.FC<Props> = ({ open, onClose, onSelect }) => {
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const previewBlobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Query for public tracks that allow use in reels
        const res = await listMusicTracks({ isPublic: true, allowReels: true, status: 'approved', limit: 200 });
        const list: MusicTrack[] = res.tracks
          .filter((t) => !!t.audioURL)
          .map((t) => ({
            id: t.id,
            title: t.title || 'Untitled',
            artistName: t.artistName || 'Unknown Artist',
            artistId: t.artistId || '',
            audioURL: t.audioURL,
            isPublic: !!t.isPublic,
            allowReels: !!t.allowReels,
          }));
        setTracks(list);
        if (list.length === 0) {
          setError('No music tracks available yet. Artists can upload tracks to the Music Library.');
        }
      } catch (e: any) {
        console.error('Music library load error:', e);
        setError(e?.message || 'Failed to load music. This may require creating a database index.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tracks;
    return tracks.filter((t) =>
      t.title.toLowerCase().includes(q) || t.artistName.toLowerCase().includes(q)
    );
  }, [tracks, search]);

  useEffect(() => {
    if (!open) {
      try { previewAudioRef.current?.pause?.(); } catch {}
      if (previewBlobUrlRef.current) {
        try { URL.revokeObjectURL(previewBlobUrlRef.current); } catch {}
        previewBlobUrlRef.current = null;
      }
      return;
    }
    // set up preview audio element lazily
    if (!previewAudioRef.current) {
      previewAudioRef.current = new Audio();
      try { (previewAudioRef.current as any).crossOrigin = 'anonymous'; } catch {}
    }
    return () => {
      try { previewAudioRef.current?.pause?.(); } catch {}
      if (previewBlobUrlRef.current) {
        try { URL.revokeObjectURL(previewBlobUrlRef.current); } catch {}
        previewBlobUrlRef.current = null;
      }
    };
  }, [open]);

  const togglePreview = async (track: MusicTrack) => {
    const el = previewAudioRef.current; if (!el) return;
    try {
      if (previewingId === track.id) {
        // toggle pause
        if (!el.paused) { el.pause(); } else { await el.play().catch(() => {}); }
        return;
      }
      // switch to new track and play
      if (!apiBaseUrl) {
        throw new Error('api-base-not-configured');
      }

      const src = await fetchMusicProxyBlobUrl(track.audioURL);

      if (previewBlobUrlRef.current) {
        try { URL.revokeObjectURL(previewBlobUrlRef.current); } catch {}
      }
      previewBlobUrlRef.current = src;

      if (el.src !== src) {
        el.src = src;
      }
      setPreviewingId(track.id);
      await el.play().catch(() => {});
    } catch (e) {
      // ignore preview errors (policy / network)
    }
  };

  const handleUse = (t: MusicTrack) => {
    try { previewAudioRef.current?.pause?.(); } catch {}
    setPreviewingId(null);
    onSelect(t);
  };

  if (!open) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={{ margin: 0 }}>Select Background Music</h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={styles.content}>
          <input
            type="text"
            placeholder="Search by title or artist"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.search}
          />
          {loading ? (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', border: '1px solid #eee', borderRadius: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="skeleton skeleton-circle" style={{ width: 34, height: 34 }} />
                    <div>
                      <div className="skeleton skeleton-line" style={{ width: 140, height: 12, marginBottom: 6 }} />
                      <div className="skeleton skeleton-line" style={{ width: 100, height: 10 }} />
                    </div>
                  </div>
                  <div className="skeleton" style={{ width: 72, height: 30, borderRadius: 6 }} />
                </li>
              ))}
            </ul>
          ) : error ? (
            <div style={{ padding: 12, color: '#b71c1c' }}>{error}</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 12 }}>No tracks available.</div>
          ) : (
            <ul style={styles.list}>
              {filtered.map((t) => (
                <li key={t.id} style={styles.item}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      style={styles.playBtn}
                      onClick={() => togglePreview(t)}
                      aria-label={previewingId === t.id ? 'Pause preview' : 'Play preview'}
                    >
                      {previewingId === t.id ? '❚❚' : '►'}
                    </button>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <strong>{t.title}</strong>
                      <span style={{ color: '#5B6E6E', fontSize: 12 }}>{t.artistName}</span>
                    </div>
                  </div>
                  <button
                    style={styles.selectBtn}
                    onPointerDown={() => handleUse(t)}
                    onClick={() => handleUse(t)}
                  >{'Use'}</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 },
  modal: { background: '#fff', borderRadius: 12, width: '95%', maxWidth: 700, overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottom: '1px solid #eee' },
  closeBtn: { background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer' },
  content: { padding: 16 },
  search: { width: '100%', padding: 10, border: '2px solid #17A2A9', borderRadius: 8, marginBottom: 12 },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflow: 'auto' },
  item: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', border: '1px solid #eee', borderRadius: 8 },
  playBtn: { width: 34, height: 34, borderRadius: '50%', border: '1px solid #0B6B6F', color: '#0B6B6F', background: 'transparent', cursor: 'pointer', fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  selectBtn: { padding: '6px 12px', background: '#0B6B6F', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' },
};

export default MusicLibraryPicker;
